import React, { useState } from 'react';
import { 
  X, 
  Send, 
  User, 
  Mail, 
  Phone, 
  Heart,
  Calendar,
  MapPin,
  Church,
  MessageCircle,
  AlertCircle,
  Check,
  Users
} from 'lucide-react';

const InscriptionModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    prenom_epoux: '',
    nom_epoux: '',
    age_epoux: '',
    prenom_epouse: '',
    nom_epouse: '',
    age_epouse: '',
    date_mariage: '',
    email: '',
    telephone: '',
    adresse: '',
    ville: '',
    paroisse: '',
    motivation: '',
    accepte_engagement: false
  });
  
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.accepte_engagement) {
      showNotification('Vous devez accepter l\'engagement pour continuer', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/rejoindre`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok) {
        showNotification('Votre demande d\'inscription a été envoyée avec succès ! Nous vous recontacterons bientôt.', 'success');
        
        // Reset form
        setFormData({
          prenom_epoux: '',
          nom_epoux: '',
          age_epoux: '',
          prenom_epouse: '',
          nom_epouse: '',
          age_epouse: '',
          date_mariage: '',
          email: '',
          telephone: '',
          adresse: '',
          ville: '',
          paroisse: '',
          motivation: '',
          accepte_engagement: false
        });

        setCurrentStep(1);

        // Close modal after delay
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        showNotification(data.message || 'Une erreur est survenue lors de l\'envoi de votre demande.', 'error');
      }
    } catch (error) {
      console.error('Inscription error:', error);
      showNotification('Erreur de connexion. Veuillez réessayer plus tard.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return formData.prenom_epoux && formData.nom_epoux && formData.age_epoux && 
               formData.prenom_epouse && formData.nom_epouse && formData.age_epouse && 
               formData.date_mariage;
      case 2:
        return formData.email;
      case 3:
        return formData.accepte_engagement;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-amber-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <Heart className="w-6 h-6 mr-3" />
                Rejoignez la famille FSF
              </h2>
              <p className="text-blue-100 mt-1">
                Communauté de couples chrétiens au Bénin
              </p>
              <div className="mt-3 text-sm text-blue-100">
                Étape {currentStep} sur 3
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="bg-gray-100 p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-semibold text-gray-600">Progression</span>
            <span className="text-sm text-gray-600">{Math.round((currentStep / 3) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-300 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-blue-500 to-amber-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Notification */}
          {notification.show && (
            <div className={`p-4 rounded-lg flex items-center space-x-3 ${
              notification.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {notification.type === 'success' ? (
                <Check className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span className="text-sm">{notification.message}</span>
            </div>
          )}

          {/* Étape 1: Informations du couple */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Users className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">Informations du couple</h3>
                <p className="text-gray-600 text-justify">Parlez-nous de votre union bénie</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-justify">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Prénom de l'époux *
                  </label>
                  <input
                    type="text"
                    name="prenom_epoux"
                    value={formData.prenom_epoux}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Prénom de l'époux"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Nom de l'époux *
                  </label>
                  <input
                    type="text"
                    name="nom_epoux"
                    value={formData.nom_epoux}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Nom de famille"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Prénom de l'épouse *
                  </label>
                  <input
                    type="text"
                    name="prenom_epouse"
                    value={formData.prenom_epouse}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Prénom de l'épouse"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Nom de l'épouse *
                  </label>
                  <input
                    type="text"
                    name="nom_epouse"
                    value={formData.nom_epouse}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Nom de famille"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Age de l'époux *
                  </label>
                  <input
                    type="number"
                    name="age_epoux"
                    value={formData.age_epoux}
                    onChange={handleChange}
                    required
                    min="18"
                    max="120"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Age de l'époux"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Age de l'épouse *
                  </label>
                  <input
                    type="number"
                    name="age_epouse"
                    value={formData.age_epouse}
                    onChange={handleChange}
                    required
                    min="18"
                    max="120"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Age de l'épouse"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 text-justify">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Date de mariage *
                </label>
                <input
                  type="date"
                  name="date_mariage"
                  value={formData.date_mariage}
                  onChange={handleChange}
                  required
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                />
              </div>
            </div>
          )}

          {/* Étape 2: Contact et localisation */}
          {currentStep === 2 && (
            <div className="space-y-6 text-justify">
              <div className="text-center mb-6">
                <Mail className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">Contact et localisation</h3>
                <p className="text-gray-600 text-justify">Comment vous joindre et vous situer</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-justify">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="votre.email@exemple.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="+229 XX XX XX XX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Ville
                  </label>
                  <input
                    type="text"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Cotonou, Porto-Novo, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    <Church className="w-4 h-4 inline mr-2" />
                    Paroisse
                  </label>
                  <input
                    type="text"
                    name="paroisse"
                    value={formData.paroisse}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all"
                    placeholder="Nom de votre paroisse"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MapPin className="w-4 h-4 inline mr-2" />
                  Adresse complète
                </label>
                <textarea
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all resize-vertical"
                  placeholder="Votre adresse complète"
                />
              </div>
            </div>
          )}

          {/* Étape 3: Motivation et engagement */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <Heart className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900">Motivation et engagement</h3>
                <p className="text-gray-600 text-justify">Pourquoi rejoindre la communauté FSF ?</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 text-justify">
                  <MessageCircle className="w-4 h-4 inline mr-2" />
                  Votre motivation
                </label>
                <textarea
                  name="motivation"
                  value={formData.motivation}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-500 transition-all resize-vertical"
                  placeholder="Partagez avec nous ce qui vous motive à rejoindre notre communauté de couples chrétiens..."
                />
              </div>

              {/* Engagement */}
              <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200 text-justify">
                <h4 className="text-lg font-bold text-blue-900 mb-4">Engagement de la communauté FSF</h4>
                <div className="text-blue-800 space-y-3 text-sm text-justify">
                  <p>En rejoignant notre communauté, vous vous engagez à :</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Participer activement aux activités spirituelles</li>
                    <li>Vivre selon les valeurs chrétiennes</li>
                    <li>Soutenir les autres couples dans leur cheminement</li>
                    <li>Contribuer à l'harmonie de la communauté</li>
                    <li>Respecter la devise : Amour - Fidélité - Paix</li>
                  </ul>
                </div>
                
                <div className="mt-6">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="accepte_engagement"
                      checked={formData.accepte_engagement}
                      onChange={handleChange}
                      required
                      className="w-5 h-5 text-blue-600 border-2 border-blue-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-semibold text-blue-900">
                      J'accepte cet engagement et souhaite rejoindre la famille FSF *
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between items-center pt-6 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                >
                  Précédent
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!isStepValid(currentStep)}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-amber-600 text-white rounded-lg hover:from-blue-700 hover:to-amber-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Suivant
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading || !isStepValid(currentStep)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-amber-600 text-white rounded-lg hover:from-blue-700 hover:to-amber-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>Envoyer ma demande</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* FSF Signature */}
          <div className="text-center pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 flex items-center justify-center">
              <Heart className="w-4 h-4 mr-2 text-red-500" />
              Fleurs de la Sainte Famille - Amour, Fidélité, Paix
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InscriptionModal;